import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "./styles";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  FiClipboard,
  FiSearch,
  FiFileText,
  FiTruck,
  FiUpload,
  FiTrash2,
  FiX,
  FiPlus,
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiAlertCircle,
} from "react-icons/fi";
import { GoSync } from "react-icons/go";

// ─── Rótulos das respostas (espelha o questionário) ───────────────────────────
const OPT = {
  sim: "Sim",
  nao: "Não",
  parcial: "Parcialmente",
  lucro_real: "Lucro Real",
  lucro_presumido: "Lucro Presumido",
  simples: "Simples Nacional",
  mei: "MEI",
  outro: "Outro",
  hibrido: "Regime híbrido (IBS/CBS por fora do DAS)",
  simples_tradicional: "Simples Nacional tradicional",
  migracao: "Migração para Lucro Presumido/Real",
  varios: "Sim, há vários no mercado",
  poucos: "Sim, mas poucos",
  unico: "Não — fornecedor praticamente único",
};

const lbl = (v) => (v === "" || v === null || v === undefined ? "—" : OPT[v] || v);

const QA_SECTIONS = [
  {
    title: "Continuidade",
    fields: [
      ["q1_continua", "1. Pretende continuar fornecendo em 2027+?"],
    ],
  },
  {
    title: "Identificação",
    fields: [
      ["q2_razaoSocial", "2. Razão social"],
      ["q3_cnpj", "3. CNPJ"],
      ["q4_codigo", "4. Código do fornecedor"],
      ["q5_representante", "5. Representante legal"],
      ["q6_cargo", "6. Cargo / função"],
      ["q7_cpf", "7. CPF do representante"],
      ["q8_contato", "8. E-mail e telefone"],
      ["q9_contatoFiscal", "9. Contato fiscal/tributário"],
      ["q10_negocio", "10. O que vende / comprador / o que gostaria de vender"],
    ],
  },
  {
    title: "Dados bancários",
    fields: [
      ["q11_banco", "11. Banco"],
      ["q11_agencia", "11. Agência"],
      ["q11_conta", "11. Conta"],
      ["q11_pix", "11. Chave PIX"],
    ],
  },
  {
    title: "Regime tributário",
    fields: [
      ["q12_regime", "12. Regime de tributação atual"],
      ["q12_outro", "12. Outro regime (descrição)"],
      ["q13_regimeIbsCbs", "13. Regime de apuração IBS/CBS a partir de 2027"],
      ["q14_cienteHibrido", "14. Ciente do prazo do regime híbrido (30/09/2026)"],
      ["q15_regimesEspeciais", "15. Regimes especiais / benefícios fiscais"],
      ["q15_descricao", "15. Descrição dos benefícios"],
      ["q16_regularidade", "16. Regularidade fiscal (CND)"],
      ["q16_motivo", "16. Motivo / casos em aberto"],
    ],
  },
  {
    title: "Notas fiscais",
    fields: [
      ["q17_emiteNf", "17. Preparada para emitir NF de todas as receitas"],
      ["q17_previsao", "17. Previsão (se não)"],
      ["q18_prazoAdaptacao", "18. Prazo legal de adaptação"],
    ],
  },
  {
    title: "Fornecimento e capacidade",
    fields: [
      ["q19_fornecedoresAlternativos", "19. Fornecedores alternativos no mercado"],
      ["q20_prazoEntrega", "20. Prazo médio de entrega/atendimento"],
      ["q20_capacidadeDemanda", "20. Capacidade para aumento de demanda"],
    ],
  },
  {
    title: "Condições e observações",
    fields: [
      ["q21_aceite", "21. Aceitou as condições sem ressalvas"],
      ["q22_observacoes", "22. Observações"],
    ],
  },
];

const fmtCnpj = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length !== 14) return v || "—";
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function AutomecFornecedores() {
  const [tab, setTab] = useState("respostas");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [respostasTotal, setRespostasTotal] = useState(0);
  const [fornecedores, setFornecedores] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addForm, setAddForm] = useState({ cnpj: "", razaoSocial: "", codigo: "", contatoNome: "", contatoEmail: "" });
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get("/automec/stats");
      setStats(r.data.stats);
    } catch { /* silencioso — KPIs não são críticos */ }
  }, []);

  const fetchRespostas = useCallback(async (q = "") => {
    try {
      const r = await api.get("/automec/respostas", { params: { size: 200, q } });
      setRespostas(r.data.items || []);
      setRespostasTotal(r.data.total || 0);
    } catch {
      toast.error("Erro ao carregar respostas");
    }
  }, []);

  const fetchFornecedores = useCallback(async () => {
    try {
      const r = await api.get("/automec/fornecedores");
      setFornecedores(r.data.items || []);
    } catch {
      toast.error("Erro ao carregar fornecedores");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchRespostas(), fetchFornecedores()]);
      setLoading(false);
    })();
  }, [fetchStats, fetchRespostas, fetchFornecedores]);

  useEffect(() => {
    const t = setTimeout(() => fetchRespostas(search), 350);
    return () => clearTimeout(t);
  }, [search, fetchRespostas]);

  const openDetail = async (item) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const r = await api.get(`/automec/respostas/${item.id}`);
      setDetail(r.data.item);
    } catch {
      toast.error("Erro ao carregar resposta");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteResposta = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/automec/respostas/${deleteConfirm.id}`);
      toast.success("Resposta excluída — o fornecedor pode reenviar");
      setDeleteConfirm(null);
      fetchRespostas(search);
      fetchStats();
    } catch {
      toast.error("Erro ao excluir resposta");
    }
  };

  const handleDeleteFornecedor = async (f) => {
    try {
      await api.delete(`/automec/fornecedores/${f.id}`);
      toast.success("Fornecedor removido");
      fetchFornecedores();
      fetchStats();
    } catch {
      toast.error("Erro ao remover fornecedor");
    }
  };

  const handleAddFornecedor = async () => {
    if (!addForm.cnpj || !addForm.razaoSocial) {
      toast.error("CNPJ e razão social são obrigatórios");
      return;
    }
    try {
      await api.post("/automec/fornecedores", addForm);
      toast.success("Fornecedor cadastrado");
      setAddForm({ cnpj: "", razaoSocial: "", codigo: "", contatoNome: "", contatoEmail: "" });
      fetchFornecedores();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.error || "Erro ao cadastrar");
    }
  };

  const parsedImport = useMemo(() => {
    if (!importText.trim()) return [];
    return importText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/[;,\t]/).map((p) => p.trim());
        return {
          cnpj: parts[0] || "",
          razaoSocial: parts[1] || "",
          codigo: parts[2] || "",
          contatoNome: parts[3] || "",
          contatoEmail: parts[4] || "",
        };
      });
  }, [importText]);

  const handleImport = async () => {
    if (parsedImport.length === 0) {
      toast.error("Cole a lista de fornecedores primeiro");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/automec/fornecedores/import", { fornecedores: parsedImport });
      const d = r.data;
      toast.success(`Importação: ${d.inseridos} novos, ${d.atualizados} atualizados, ${d.invalidos.length} inválidos`);
      setImportText("");
      fetchFornecedores();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.error || "Erro na importação");
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImportText(String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  };

  const lastResponse = respostas[0]?.submittedAt;

  return (
    <Container>
      {/* HEADER */}
      <div className="header-info">
        <div className="header-icon"><FiClipboard /></div>
        <div className="header-details">
          <h3>Questionário de Fornecedores — Reforma Tributária</h3>
          <div className="header-meta">
            <span className="status-tag">AUTOMEC COMERCIAL</span>
            <span className="update-tag">
              {lastResponse ? `Última resposta: ${fmtDate(lastResponse)}` : "Aguardando respostas"}
            </span>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="tabs-header">
        <button className={`tab ${tab === "respostas" ? "active" : ""}`} onClick={() => setTab("respostas")}>
          <FiFileText /> Respostas
        </button>
        <button className={`tab ${tab === "fornecedores" ? "active" : ""}`} onClick={() => setTab("fornecedores")}>
          <FiTruck /> Base de Fornecedores
        </button>
        <button className={`tab ${tab === "importar" ? "active" : ""}`} onClick={() => setTab("importar")}>
          <FiUpload /> Importar
        </button>
      </div>

      {/* KPIs */}
      <div className="kpi-cards">
        <div className="kpi-card primary">
          <div className="card-content">
            <div className="thumb"><FiFileText /></div>
            <div className="text">
              <span className="kpi-label">Respostas recebidas</span>
              <span className="kpi-value">{loading ? "—" : stats?.totalRespostas ?? respostasTotal}</span>
            </div>
          </div>
        </div>
        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb"><FiTruck /></div>
            <div className="text">
              <span className="kpi-label">Fornecedores na base</span>
              <span className="kpi-value">{loading ? "—" : stats?.totalFornecedores ?? fornecedores.length}</span>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><FiCheckCircle /></div>
            <div className="text">
              <span className="kpi-label">Respondidos (da base)</span>
              <span className="kpi-value">{loading ? "—" : stats?.respondidos ?? 0}</span>
            </div>
          </div>
        </div>
        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><FiClock /></div>
            <div className="text">
              <span className="kpi-label">Pendentes</span>
              <span className="kpi-value">{loading ? "—" : stats?.pendentes ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* TAB: RESPOSTAS */}
      {tab === "respostas" && (
        <>
          <div className="toolbar">
            <div className="toolbar-left">
              <button className="toolbar-btn"><FiFileText /> {respostasTotal} resposta(s)</button>
              {stats?.avulsas > 0 && (
                <span className="badge amber"><FiAlertCircle /> {stats.avulsas} avulsa(s) — CNPJ fora da base</span>
              )}
              <div className="search-box">
                <FiSearch />
                <input
                  placeholder="Buscar por razão social ou CNPJ..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="table-wrapper">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Razão Social</th>
                    <th style={{ width: "15%" }}>CNPJ</th>
                    <th style={{ width: "15%" }}>Regime</th>
                    <th style={{ width: "12%" }}>Continua?</th>
                    <th style={{ width: "16%" }}>Enviado em</th>
                    <th style={{ width: "12%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {respostas.length === 0 && (
                    <tr><td colSpan={6}><div className="empty-state"><FiFileText /><div>Nenhuma resposta recebida ainda</div></div></td></tr>
                  )}
                  {respostas.map((r) => (
                    <tr key={r.id} onClick={() => openDetail(r)}>
                      <td>
                        <div className="cell-main">{r.razaoSocial}</div>
                        <div className="cell-sub">{r.representante || ""}</div>
                      </td>
                      <td className="cell-mono">{fmtCnpj(r.cnpj)}</td>
                      <td>{lbl(r.regimeTributario)}</td>
                      <td>
                        <span className={`badge ${r.continuaFornecendo === "sim" ? "green" : "red"}`}>
                          {r.continuaFornecendo === "sim" ? "Sim" : "Não"}
                        </span>
                      </td>
                      <td className="cell-sub">{fmtDate(r.submittedAt)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button className="icon-btn danger" title="Excluir resposta" onClick={() => setDeleteConfirm(r)}>
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* TAB: FORNECEDORES */}
      {tab === "fornecedores" && (
        <>
          <div className="add-form">
            <input placeholder="CNPJ" value={addForm.cnpj} onChange={(e) => setAddForm({ ...addForm, cnpj: e.target.value })} />
            <input placeholder="Razão social" value={addForm.razaoSocial} onChange={(e) => setAddForm({ ...addForm, razaoSocial: e.target.value })} />
            <input placeholder="Código" value={addForm.codigo} onChange={(e) => setAddForm({ ...addForm, codigo: e.target.value })} />
            <input placeholder="Contato" value={addForm.contatoNome} onChange={(e) => setAddForm({ ...addForm, contatoNome: e.target.value })} />
            <input placeholder="E-mail" value={addForm.contatoEmail} onChange={(e) => setAddForm({ ...addForm, contatoEmail: e.target.value })} />
            <button className="toolbar-btn primary" onClick={handleAddFornecedor}><FiPlus /> Adicionar</button>
          </div>

          <div className="table-wrapper">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "34%" }}>Razão Social</th>
                    <th style={{ width: "15%" }}>CNPJ</th>
                    <th style={{ width: "10%" }}>Código</th>
                    <th style={{ width: "18%" }}>Contato</th>
                    <th style={{ width: "13%" }}>Status</th>
                    <th style={{ width: "10%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {fornecedores.length === 0 && (
                    <tr><td colSpan={6}><div className="empty-state"><FiTruck /><div>Nenhum fornecedor na base — use a aba Importar</div></div></td></tr>
                  )}
                  {fornecedores.map((f) => (
                    <tr key={f.id} style={{ cursor: "default" }}>
                      <td className="cell-main">{f.razaoSocial}</td>
                      <td className="cell-mono">{fmtCnpj(f.cnpj)}</td>
                      <td className="cell-sub">{f.codigo || "—"}</td>
                      <td className="cell-sub">{f.contatoNome || f.contatoEmail || "—"}</td>
                      <td>
                        {f.status === "respondido" ? (
                          <span className="badge green"><FiCheckCircle /> Respondido</span>
                        ) : (
                          <span className="badge gray"><FiClock /> Pendente</span>
                        )}
                      </td>
                      <td>
                        <button className="icon-btn danger" title="Remover" onClick={() => handleDeleteFornecedor(f)}>
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* TAB: IMPORTAR */}
      {tab === "importar" && (
        <div className="import-card">
          <div className="hint">
            Cole a lista de fornecedores — uma linha por fornecedor, campos separados por <code>;</code>, <code>,</code> ou TAB:<br />
            <code>cnpj;razão social;código interno;contato;e-mail</code> — apenas CNPJ e razão social são obrigatórios.
            {parsedImport.length > 0 && <> <strong>{parsedImport.length} linha(s) detectada(s)</strong>.</>}
          </div>
          <textarea
            placeholder={"00.000.000/0000-00;FORNECEDOR EXEMPLO LTDA;F001;João;joao@fornecedor.com\n11.111.111/0001-11;OUTRA INDUSTRIA SA;F002;Maria;maria@industria.com"}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
          />
          <div className="import-actions">
            <label className="toolbar-btn" style={{ cursor: "pointer" }}>
              <FiUpload /> Carregar arquivo (.csv/.txt)
              <input type="file" accept=".csv,.txt" style={{ display: "none" }} onChange={handleFileUpload} />
            </label>
            <button className="toolbar-btn primary" onClick={handleImport} disabled={importing}>
              {importing ? <><GoSync className="spin" /> Importando...</> : <><FiCheckCircle /> Importar {parsedImport.length > 0 ? `${parsedImport.length} fornecedores` : ""}</>}
            </button>
          </div>
        </div>
      )}

      {/* MODAL DETALHE */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h4>{detail.loading ? "Carregando..." : detail.razaoSocial}</h4>
                {!detail.loading && (
                  <div className="sub">{fmtCnpj(detail.cnpj)} · Protocolo {detail.id}</div>
                )}
              </div>
              <button className="icon-btn" onClick={() => setDetail(null)}><FiX /></button>
            </div>

            <div className="modal-body">
              {(detailLoading || detail.loading) ? (
                <div className="empty-state"><GoSync className="spin" /><div>Carregando resposta...</div></div>
              ) : (
                <>
                  {QA_SECTIONS.map((sec) => (
                    <div className="detail-section" key={sec.title}>
                      <div className="ds-title">{sec.title}</div>
                      {sec.fields.map(([key, label]) => {
                        const v = detail.respostas?.[key];
                        if (v === undefined || v === "") return null;
                        const val = key === "q21_aceite" ? (v ? "Sim — li e concordo" : "Não")
                                  : key === "q3_cnpj" ? fmtCnpj(v) : lbl(v);
                        return (
                          <div className="qa" key={key}>
                            <div className="qq">{label}</div>
                            <div className="aa">{val}</div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  <div className="detail-section">
                    <div className="ds-title">Assinatura</div>
                    <div className="qa">
                      <div className="qq">Local e data</div>
                      <div className="aa">{detail.respostas?.localData || "—"}</div>
                    </div>
                    <div className="qa">
                      <div className="qq">Nome</div>
                      <div className="aa">{detail.respostas?.assinaturaNome || "—"}</div>
                    </div>
                    <div className="qa">
                      <div className="qq">CPF</div>
                      <div className="aa">{detail.respostas?.assinaturaCpf || "—"}</div>
                    </div>
                    <div className="qa">
                      <div className="qq">Cargo</div>
                      <div className="aa">{detail.respostas?.assinaturaCargo || "—"}</div>
                    </div>
                    {detail.assinaturaDataUrl && (
                      <div className="signature-box">
                        <img src={detail.assinaturaDataUrl} alt="Assinatura do representante" />
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <div className="ds-title">Registro eletrônico</div>
                    <div className="meta-grid">
                      <div className="meta-item"><div className="k">Enviado em</div><div className="v">{fmtDate(detail.submittedAt)}</div></div>
                      <div className="meta-item"><div className="k">IP</div><div className="v">{detail.ip || "—"}</div></div>
                      <div className="meta-item" style={{ gridColumn: "1 / -1" }}><div className="k">User-Agent</div><div className="v">{detail.userAgent || "—"}</div></div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => setDetail(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CONFIRM DELETE */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h4>Excluir resposta</h4></div>
            <div className="modal-body">
              <p style={{ fontSize: "0.88em", lineHeight: 1.6 }}>
                Excluir a resposta de <strong>{deleteConfirm.razaoSocial}</strong> ({fmtCnpj(deleteConfirm.cnpj)})?
                <br /><br />
                Após excluir, o fornecedor poderá enviar uma nova resposta pelo link /automec.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setDeleteConfirm(null)}>Cancelar</button>
              <button className="danger" onClick={handleDeleteResposta}><FiTrash2 /> Excluir</button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
